import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { UserVerificationLogService, UserVerificationService, Verification } from 'us-web-services';
import CardStyles from '../../styles/CardStyles';
import DateUtil from '../../util/DateUtil';
import IdentityCheckEdit from './IdentityCheckEdit';

interface Props {
  userId: number;
}

function IdentityCheck(props: Props) {
  const { userId } = props;
  const { classes } = CardStyles();
  const [verification, setVerification] = useState<Verification>({});
  const [status, setStatus] = useState('');
  const [verificationLogs, setVerificationLogs] = useState<Verification[]>([]);
  const [loaded, setLoaded] = useState(false);

  const getVerification = useCallback(async () => {
    const verificationResponse = (await UserVerificationService.get(userId)).data;

    setStatus(verificationResponse.data.status);
    setVerification(verificationResponse.data);
    setLoaded(true);
  }, [userId]);

  const getVerificationLogs = useCallback(async () => {
    const logResponse = await UserVerificationLogService.findByFilter(
      `?userId=${userId}&limit=50&sort=updated&updated.dir=desc`,
    );

    setVerificationLogs(logResponse.data.data);
  }, [userId]);

  const getStatusTitle = () => {
    let statusTitle = 'Failed';

    if (status === 'pass') {
      statusTitle = 'Completed';
    } else if (status === 'incomplete') {
      statusTitle = 'Incomplete';
    }

    return statusTitle;
  };

  const onUpdate = () => {
    setStatus(verification.status);
    getStatusTitle();
    getVerificationLogs();
  };

  useEffect(() => {
    getVerification();
    getVerificationLogs();
  }, [getVerification, getVerificationLogs]);

  return (
    <Card className={classes.sectionCard}>
      <CardContent className={classes.sectionContent}>
        {verification.bypassedByCompany && (
          <Alert severity='info' style={{ marginBottom: 16 }}>
            Identity check bypassed — waived by company configuration
            {verification.bypassedByCompanyName ? ` (${verification.bypassedByCompanyName})` : ''}.
          </Alert>
        )}
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 220 }}>
            <Typography className={classes.sectionHeader}>
              Identity Check
              {loaded && (
                <IdentityCheckEdit
                  userId={userId}
                  identityCheck={verification}
                  setIdentityCheck={setVerification}
                  onUpdate={onUpdate}
                  buttonLabel='Edit'
                />
              )}
            </Typography>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Status</span>
              <span className={classes.infoValue}>{getStatusTitle()}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Updated</span>
              <span className={classes.infoValue}>
                {DateUtil.getReadableDateTime(verification.updated)}
              </span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Updated by</span>
              <span className={classes.infoValue}>{verification.updatedBy}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Attempts</span>
              <span className={classes.infoValue}>{verification.attempts}</span>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 300 }}>
            <Typography className={classes.sectionHeader}>
              Identity Check Logs
            </Typography>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Updated by</TableCell>
                  <TableCell>Updated on</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {verificationLogs &&
                  verificationLogs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell>{log.updatedAgent}</TableCell>
                      <TableCell>
                        {DateUtil.getReadableDateTime(log.updated)}
                      </TableCell>
                      <TableCell>{log.status}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default IdentityCheck;
